import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class16MatchingSentencePractice01 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(11);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 14));
  };

  // Original answer options with their positions
    const originalAnswerOptions = [
        "was the variety of experimental techniques used",
        "was not of interest to young islanders today",
        "was not conclusive evidence in support of a single theory",
        "was being able to change their practices when necessary",
        "was the first time humans intentionally crossed an ocean",
        "was the speed with which it was conducted"
    ];

  // Available answer options (filtered from original list)
  const [answerOptions, setAnswerOptions] = useState([...originalAnswerOptions]);

  // Track the answers dropped in each question (questions 11–14)
  const [dropTargets, setDropTargets] = useState({
    11: null,
    12: null,
    13: null,
    14: null
  });

      const questions = [
        "One limitation in the information produced by all of this research is that it",
        "The best thing about this type of research",
        "The most important achievement of traditional navigators",
        "The migration of people from Asia to the Pacific"
    ];

  const handleDragStart = (e, option) => {
    e.dataTransfer.setData('option', option); // Store the dragged option
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, questionNum) => {
    e.preventDefault();
    const option = e.dataTransfer.getData('option');

    if (answerOptions.includes(option)) {
      // Remove the option from available options and add to the drop target
      setAnswerOptions(prev => prev.filter(item => item !== option));
      setDropTargets(prev => ({ ...prev, [questionNum]: option }));
    }
  };

  const handleRemoveAnswer = (questionNum) => {
    const answer = dropTargets[questionNum];

    if (answer) {
      // Remove the answer from the drop zone
      setDropTargets(prev => ({ ...prev, [questionNum]: null }));

      // Add back to the stack in its original position
      setAnswerOptions(prev => {
        const newOptions = [...prev];
        const originalIndex = originalAnswerOptions.indexOf(answer);

        // Find the correct position to insert the item back
        let insertIndex = 0;
        for (let i = 0; i < originalIndex; i++) {
          if (newOptions.includes(originalAnswerOptions[i])) {
            insertIndex++;
          }
        }

        newOptions.splice(insertIndex, 0, answer);
        return newOptions;
      });
    }
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>
      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4 text-center">Pacific navigation and voyaging</h2>

              {/* First paragraph - centered, italic */}
              <p className="text-[14px] italic font-semibold mb-4 text-center">
                How people migrated to the Pacific islands
              </p>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  The many tiny islands of the Pacific Ocean had no human population until ancestors of today’s islanders sailed from Southeast Asia in ocean-going canoes approximately 2,000 years ago. At the present time, the debate continues about exactly how they migrated such vast distances across the ocean, without any of the modern technologies we take for granted.
                </p>
                <p>
                  Although the romantic vision of some early twentieth-century writers of fleets of heroic navigators simultaneously setting sail had come to be considered by later investigators to be exaggerated, no considered assessment of Pacific voyaging was forthcoming until 1956 when the American historian Andrew Sharp published his research. Sharp challenged the ‘heroic vision’ by asserting that the expertise of the navigators was limited, and that the settlement of the islands was not systematic, being more dependent on good fortune by drifting canoes. Sharp’s theory was widely challenged, and deservedly so. If nothing else, however, it did spark renewed interest in the topic and precipitated valuable new research.
                </p>
                <p>
                  Since the 1960s a wealth of investigations has been conducted, and most of them, thankfully, have been of the ‘non-armchair’ variety. While it would be wrong to denigrate all ‘armchair’ research - that based on an examination of available published materials - it has turned out that so little progress had been made in the area of Pacific voyaging because most writers relied on the same old sources - travellers’ journals or missionary narratives compiled by unskilled observers. After Sharp, this began to change, and researchers conducted most of their investigations not in libraries, but in the field.
                </p>
                <p>
                  In 1965, David Lewis, a physician and experienced yachtsman, set to work using his own unique philosophy: he took the yacht he had owned for many years and navigated through the islands in order to contact those men who still find their way at sea using traditional methods. He then accompanied these men, in their traditional canoes, on test voyages from which all modern instruments were banished from sight, though Lewis secretly used them to confirm the navigator’s calculations. His most famous such voyage was a return trip of around 1,000 nautical miles between two islands in mid¬ocean. Far from drifting, as proposed by Sharp, Lewis found that ancient navigators would have known which course to steer by memorizing which stars rose and set in certain positions along the horizon and this gave them fixed directions by which to steer their boats.
                </p>
                <p>
                  The geographer Edwin Doran followed a quite different approach. He was interested in obtaining exact data on canoe sailing performance, and to that end employed the latest electronic instrumentation. Doran travelled on board traditional sailing canoes in some of the most remote parts of the Pacific, all the while using his instruments to record canoe speeds in different wind strengths - from gales to calms - the angle canoes could sail relative to the wind. In the process, he provided the first really precise attributes of traditional sailing canoes.
                </p>
                <p>
                  A further contribution was made by Steven Horvath. As a physiologist, Horvath’s interest was not in navigation techniques or in canoes, but in the physical capabilities of the men themselves. By adapting standard physiological techniques, Horvath was able to calculate the energy expenditure required to paddle canoes of this sort at times when there was no wind to fill the sails, or when the wind was contrary. He concluded that paddles, or perhaps long oars, could indeed have propelled for long distances what were primarily sailing vessels.
                </p>
                <p>
                  Finally, a team led by p Wall Garrard conducted important research, in this case by making investigations while remaining safely in the laboratory. Wall Garrard’s unusual method was to use the findings of linguists who had studied the languages of the Pacific islands, many of which are remarkably similar although the islands where they are spoken are sometimes thousands of kilometres apart. Clever adaptation of computer simulation techniques pioneered in other disciplines allowed him to produce convincing models suggesting the migrations were indeed systematic, but not simultaneous. Wall Garrard proposed the migrations should be seen not as a single journey made by a massed fleet of canoes, but as a series of ever more ambitious voyages, each pushing further into the unknown ocean.
                </p>
                <p>
                  What do we learn about Pacific navigation and voyaging from this research? Quite correctly, none of the researchers tried to use their findings to prove one theory or another; experiments such as these cannot categorically confirm or negate a hypothesis. The strength of this research lay in the range of methodologies employed. When we splice together these findings, we can propose that traditional navigators used a variety of canoe types, sources of water and navigation techniques, and it was this adaptability which was their greatest accomplishment. These navigators observed the conditions prevailing at sea at the time a voyage was made and altered their techniques accordingly. Furthermore, the canoes of the navigators were not drifting helplessly at sea but were most likely part of a systematic migration; as such, the Pacific peoples were able to view the ocean as an avenue, not a barrier, to communication before any other race on Earth. Finally, one unexpected but most welcome consequence of this research has been a renaissance in the practice of traditional voyaging. In some groups of islands in the Pacific today young people are resurrecting the skills of their ancestors, when a few decades ago it seemed they would be lost forever.
                </p>
              </div>
            </div>
          </div>







          {/* Right: Questions */}
          <div
            ref={rightColRef}
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
          >
            {/* Questions 11–14 */}
            <div className="mb-10 w-full">
              <h3 className="text-[16px] font-bold mb-4 text-left">Questions 11 – 14</h3>
              <p className="text-[14px] mb-6 text-left">
                Complete each sentence with the correct ending. Choose the correct ending and move it into the gap.
              </p>

              {/* Question sentences with inline drop boxes */}
              <div className="space-y-4 mb-8">
                {questions.map((question, index) => {
                  const questionNum = 11 + index;
                  const hasAnswer = dropTargets[questionNum];

                  return (
                    <div key={questionNum} className="text-[14px] leading-relaxed">
                      {question}{' '}
                      <span
                        className="inline-block w-[150px] h-6 bg-[#E0F2FE] border border-[#7DD3FC] align-middle cursor-pointer text-center"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, questionNum)}
                        onClick={hasAnswer ? () => handleRemoveAnswer(questionNum) : undefined}
                      >
                        <input
                          type="text"
                          className={`w-full h-full bg-transparent text-center text-[12px] font-semibold outline-none placeholder:font-semibold placeholder:text-black placeholder:opacity-100 focus:placeholder:opacity-0 ${hasAnswer ? 'text-black font-bold' : ''
                            }`}
                          placeholder={hasAnswer ? hasAnswer : `${questionNum}`}
                          readOnly={true}
                        />
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Draggable answer options */}
              <div className="flex flex-wrap gap-2">
                {answerOptions.map((option, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, option)}
                    className="inline-block px-3 py-1.5 bg-[#e9f0f7] text-[13px] rounded cursor-move border border-gray-300 shadow-sm hover:bg-[#d8e6f2] transition-colors duration-150"
                  >
                    {option}
                  </div>
                ))}
              </div>
            </div>
          </div>














        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-[#D7DFEC] border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">
        {/* Left: Question Buttons */}
        <div className="flex items-center space-x-1">
          {[11, 12, 13, 14].map((questionNumber) => (
            <button
              key={questionNumber}
              onClick={() => setCurrentQuestion(questionNumber)}
              className={`w-7 h-7 mx-0.5 rounded text-[12px] font-bold flex items-center justify-center ${currentQuestion === questionNumber
                ? 'bg-blue-500 text-white'
                : 'bg-black text-white'
                }`}
            >
              {questionNumber}
            </button>
          ))}
        </div>

        {/* Right: Arrows */}
        <div className="flex items-center space-x-2">
          {/* Backward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.max(11, prev - 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 11 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Previous Question"
            disabled={currentQuestion === 11}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Forward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.min(14, prev + 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 14 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Next Question"
            disabled={currentQuestion === 14}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>







    </div>
  );
};

export default Class16MatchingSentencePractice01;
